if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.setupInlineImageZoom();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const zoomedMedia = this.elements.viewer.querySelector('.product__media-item.is-inline-zoomed');
      if (zoomedMedia && zoomedMedia !== event.detail.currentElement) this.resetInlineImageZoom();
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      this.resetInlineImageZoom();
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setupInlineImageZoom() {
      if (!this.elements.viewer) return;

      this.imageZoomButtons = this.elements.viewer.querySelectorAll('.product__modal-opener--image .product__media-toggle');
      this.imageZoomButtons.forEach((button) => {
        button.removeAttribute('aria-haspopup');
        button.setAttribute('aria-label', 'Zoom product image');
        button.setAttribute('aria-pressed', 'false');
      });

      this.elements.viewer.addEventListener('click', this.onInlineImageZoom.bind(this), true);
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.resetInlineImageZoom();
      });
    }

    onInlineImageZoom(event) {
      const button = event.target.closest('.product__modal-opener--image .product__media-toggle');
      if (!button || !this.elements.viewer.contains(button)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const mediaItem = button.closest('.product__media-item');
      const image = mediaItem?.querySelector('.product__modal-opener--image .product__media > img');
      if (!mediaItem || !image) return;

      const willZoom = !mediaItem.classList.contains('is-inline-zoomed');
      this.resetInlineImageZoom();

      if (willZoom) {
        const bounds = image.getBoundingClientRect();
        const isKeyboardClick = event.detail === 0 || (event.clientX === 0 && event.clientY === 0);
        const originX = isKeyboardClick ? 50 : ((event.clientX - bounds.left) / bounds.width) * 100;
        const originY = isKeyboardClick ? 50 : ((event.clientY - bounds.top) / bounds.height) * 100;
        mediaItem.style.setProperty('--luv-inline-zoom-x', `${Math.max(0, Math.min(100, originX))}%`);
        mediaItem.style.setProperty('--luv-inline-zoom-y', `${Math.max(0, Math.min(100, originY))}%`);
        mediaItem.classList.add('is-inline-zoomed');
        button.setAttribute('aria-pressed', 'true');
        button.setAttribute('aria-label', 'Reset product image zoom');
      }
    }

    resetInlineImageZoom() {
      this.elements.viewer?.querySelectorAll('.product__media-item.is-inline-zoomed').forEach((mediaItem) => {
        mediaItem.classList.remove('is-inline-zoomed');
        mediaItem.style.removeProperty('--luv-inline-zoom-x');
        mediaItem.style.removeProperty('--luv-inline-zoom-y');
      });
      this.imageZoomButtons?.forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', 'Zoom product image');
      });
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
